import { EvaluationContext } from '@openfeature/web-sdk';

/**
 * Builds the OpenFeature evaluation context shared by both Vue apps (TL-3952).
 *
 * The one canonical, vendor-neutral shape: flat primitives coerced to strings, which each provider
 * reshapes in `feature-flags.ts`. Do not encode a vendor's layout here — that is what keeps the
 * targeting policy identical on both tiers.
 *
 * TARGETING-KEY POLICY — must stay identical to lms-service: company id, then username, then none.
 * Mirrors `FeatureFlagService::targetingKey()`, because rollouts here are per tenant. Both tiers
 * emit the raw id with no prefix ("42", not "user-42"): a percentage rollout only resolves to the
 * same variant on both tiers when the key is byte-identical. Changing it re-buckets every subject
 * mid-rollout, so it must ship on both tiers together.
 */

export interface FeatureFlagSubject {
  userId?: number | string | null;
  username?: string | null;
  companyId?: number | string | null;
  role?: string | null;
}

const present = (value?: number | string | null): boolean => (
  value !== null && value !== undefined && value !== ''
);

/**
 * Returns undefined pre-login, matching the backend's null key. Fabricating a placeholder instead
 * would put every anonymous visitor in the same rollout bucket.
 */
export const buildTargetingKey = (subject: FeatureFlagSubject = {}): string | undefined => {
  if (present(subject.companyId)) {
    return String(subject.companyId);
  }

  if (present(subject.username)) {
    return String(subject.username);
  }

  return undefined;
};

export default function buildEvaluationContext(subject: FeatureFlagSubject = {}): EvaluationContext {
  const context: EvaluationContext = {
    environment: import.meta.env.VITE_APP_ENV || 'local',
  };

  const targetingKey = buildTargetingKey(subject);

  if (targetingKey !== undefined) {
    context.targetingKey = targetingKey;
  }

  // Added only when present, so rules can tell "absent" from "empty". Names are snake_case to match
  // the backend exactly: rules reference them literally, and a mismatch fails silently.
  if (present(subject.userId)) {
    context.user_id = String(subject.userId);
  }

  if (present(subject.username)) {
    context.username = String(subject.username);
  }

  if (present(subject.companyId)) {
    context.company_id = String(subject.companyId);
  }

  // The backend omits the role (resolving it would hit the DB on every evaluation), so a user_role
  // rule matches on the frontend only — prefer company_id for cross-tier rules.
  if (subject.role) {
    context.user_role = String(subject.role);
  }

  return context;
}
